import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { supabase } from '../../lib/supabase';
import { CreditCardIcon, CheckCircleIcon, MailIcon } from '../ui/Icons';
import './ResultsScreen.css';

function fmt(n) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
}

function fmtDollar(n) {
  return '$' + fmt(Math.abs(n));
}

function darkenHex(hex, amount = 0.22) {
  try {
    const h = hex.replace('#', '');
    const num = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
    const r = Math.max(0, (num >> 16) - Math.round(255 * amount));
    const g = Math.max(0, ((num >> 8) & 0xff) - Math.round(255 * amount));
    const b = Math.max(0, (num & 0xff) - Math.round(255 * amount));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  } catch { return hex; }
}

const SITE_URL = process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

function buildFullReportUrl(results, form, lead, forPopup = false, installerConfig = null) {
  try {
    // Include a minimal config snapshot (c) so the popup page can apply branding
    const c = installerConfig ? {
      primaryColor: installerConfig.primaryColor,
      accentColor: installerConfig.accentColor,
      companyName: installerConfig.companyName,
      ctaButtonText: installerConfig.ctaButtonText,
      ctaButtonUrl: installerConfig.ctaButtonUrl,
      ctaPhone: installerConfig.ctaPhone,
      ctaHeadline: installerConfig.ctaHeadline,
      fontFamily: installerConfig.fontFamily,
    } : null;
    const payload = JSON.stringify({ r: results, b: form.monthlyBill, s: form.state || results.inputs?.state, p: lead?.paymentMethod, c });
    const hash = btoa(unescape(encodeURIComponent(payload)));
    return `${SITE_URL}/results${forPopup ? '?popup=1' : ''}#${hash}`;
  } catch {
    return `${SITE_URL}/`;
  }
}

/* ── Shared report content (used by page + modal) ─────── */
function ReportContent({ results, form, lead, installerConfig, onReset, embedded, popup, internalMode, onShareEmail }) {
  const { system, cost, incentives, savings, chart } = results;
  const cta = installerConfig || {};
  const primary = cta.primaryColor || '#1b4d3e';
  const primaryDark = darkenHex(primary);
  const isCash = lead?.paymentMethod === 'cash';
  const hasFinancing = !isCash && savings.monthlyPaymentFinanced > 0;
  const daySavings = hasFinancing ? savings.netMonthlyFinanced : savings.monthly;
  const paybackYear = chart.find(d => d.cumulativeSavings >= 0)?.year;
  const state = results.inputs?.state || form?.state || '';

  const handleShare = () => {
    const url = buildFullReportUrl(results, form, lead, false, installerConfig);
    if (navigator.share) {
      navigator.share({ title: 'My Solar Savings Estimate', url }).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => alert('Link copied to clipboard!')).catch(() => {});
    }
  };

  const handlePrint = () => window.print();

  // "Email to Customer" -- only ever passed by the dashboard's "Run an
  // Estimate" tab (internalMode), so this button and its inline form only
  // appear there.
  const [shareEmailOpen, setShareEmailOpen] = useState(false);
  const [shareClientName, setShareClientName] = useState('');
  const [shareEmailValue, setShareEmailValue] = useState('');
  const [shareEmailStatus, setShareEmailStatus] = useState(''); // '' | 'sending' | 'sent' | 'error'

  const submitShareEmail = async () => {
    if (!shareEmailValue.trim() || !shareClientName.trim() || !onShareEmail || shareEmailStatus === 'sending') return;
    setShareEmailStatus('sending');
    try {
      await onShareEmail(shareEmailValue.trim(), shareClientName.trim());
      setShareEmailStatus('sent');
      setShareEmailValue('');
      setShareClientName('');
      setTimeout(() => { setShareEmailOpen(false); setShareEmailStatus(''); }, 2000);
    } catch {
      setShareEmailStatus('error');
    }
  };

  return (
    <div className="results-container">

      {/* ── HERO CARD ── */}
      <div className="results-hero-card" style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)` }}>
        <div className="hero-label">
          SOLAR SAVINGS ESTIMATE{state ? ` · ${state.toUpperCase()}` : ''}
        </div>
        <div className="hero-number">
          {fmtDollar(incentives.netCostLow)} – {fmtDollar(incentives.netCostHigh)}
        </div>
        <div className="hero-sub">
          installed cost · {fmtDollar(savings.monthly)}/mo savings · paid off in {savings.paybackYears ? `${savings.paybackYears} yrs` : 'N/A'}
        </div>
      </div>

      {/* ── COST BREAKDOWN ── */}
      <div className="results-card">
        <div className="card-section-label">COST BREAKDOWN</div>

        <div className="cost-line cost-line-net">
          <span><strong>Total installation cost</strong></span>
          <span className="cost-net-val">{fmtDollar(cost.low)} – {fmtDollar(cost.high)}</span>
        </div>

        <p className="tax-disclaimer">
          * The 30% federal residential solar tax credit (Section 25D) ended for systems placed in service after December 31, 2025, and isn't included in this estimate. State or utility incentives may still apply where you live — a local installer can confirm what's currently available.
        </p>

        {hasFinancing && (
          <div className="financing-note">
            <CreditCardIcon size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            <strong>If financed:</strong> ~{fmtDollar(savings.monthlyPaymentFinanced)}/mo loan payment (25 yr, est. 5.99% APR). Net savings from day one: <strong>~{fmtDollar(daySavings)}/mo</strong>.
          </div>
        )}

        <details className="breakdown-details">
          <summary>▸ View cost breakdown</summary>
          <div className="breakdown-list">
            {Object.entries(cost.breakdown).map(([key, val]) =>
              val > 0 && (
                <div key={key} className="breakdown-row">
                  <span>{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</span>
                  <span>{fmtDollar(val)}</span>
                </div>
              )
            )}
          </div>
        </details>
      </div>

      {/* ── SAVINGS SUMMARY ── */}
      <div className="results-card">
        <div className="card-section-label">YOUR SAVINGS SUMMARY</div>
        <div className="metrics-grid">
          {isCash ? (
            <div className="metric-card metric-green">
              <div className="metric-label">Installed Cost</div>
              <div className="metric-value">{fmtDollar(incentives.netCost)}</div>
              <div className="metric-sub">one-time cash purchase</div>
            </div>
          ) : hasFinancing && (
            <div className="metric-card metric-green">
              <div className="metric-label">Day-One Monthly Savings</div>
              <div className="metric-value">{fmtDollar(daySavings)}</div>
              <div className="metric-sub">bill − solar payment</div>
            </div>
          )}
          <div className="metric-card">
            <div className="metric-label">Annual Electricity Savings</div>
            <div className="metric-value">{fmtDollar(savings.annual)}</div>
            <div className="metric-sub">saved on your electric bill</div>
          </div>
          <div className="metric-card metric-blue">
            <div className="metric-label">30-Year Savings</div>
            <div className="metric-value">{fmtDollar(savings.thirtyYear)}</div>
            <div className="metric-sub">total vs. renting from utility</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Payback Period</div>
            <div className="metric-value">{savings.paybackYears ? `${savings.paybackYears} yrs` : 'N/A'}</div>
            <div className="metric-sub">break-even point</div>
          </div>
        </div>
      </div>

      {/* ── KEY FACTORS ── */}
      <div className="results-card">
        <div className="card-section-label">KEY FACTORS IN YOUR ESTIMATE</div>
        <div className="factors-chips">
          {form?.monthlyBill && (
            <span className="factor-chip">Monthly bill: <strong>{fmtDollar(form.monthlyBill)}</strong></span>
          )}
          {state && (
            <span className="factor-chip">State: <strong>{state}</strong></span>
          )}
          <span className="factor-chip">Payment: <strong>{isCash ? 'Cash' : 'Financed'}</strong></span>
          <span className="factor-chip">System: <strong>{system.sizeKw} kW</strong></span>
          <span className="factor-chip">Offset: <strong>{system.offsetPercent}%</strong></span>
        </div>
        <div className="state-info-box">
          <span className="state-info-icon">☀️</span>
          <span>
            <strong>{state ? `${state} solar estimate:` : 'Your estimate:'}</strong>{' '}
            A {system.sizeKw} kW system producing {fmt(system.annualProduction)} kWh/year covers {system.offsetPercent}% of your electricity — saving you {fmtDollar(savings.annual)}/year on your electric bill.
          </span>
        </div>
      </div>

      {/* ── RECOMMENDED SYSTEM ── */}
      <div className="results-card">
        <div className="card-section-label">RECOMMENDED SOLAR SYSTEM</div>
        <div className="system-grid">
          <div className="system-item">
            <span className="sys-label">System Size</span>
            <span className="sys-value">{system.sizeKw} kW</span>
          </div>
          <div className="system-item">
            <span className="sys-label">Panel Count</span>
            <span className="sys-value">{system.panelCount} panels</span>
          </div>
          <div className="system-item">
            <span className="sys-label">Annual Production</span>
            <span className="sys-value">{fmt(system.annualProduction)} kWh</span>
          </div>
          <div className="system-item">
            <span className="sys-label">Energy Offset</span>
            <span className="sys-value sys-offset">{system.offsetPercent}%</span>
          </div>
        </div>
        {/* Surfaces the backend's actual per-request data source -- the
            site's marketing copy always claims "real NREL data", but until
            now there was no way to confirm that from the live results page
            itself (dataSource was computed server-side and returned, but
            never read on the frontend). 'pvwatts' means NREL_API_KEY
            genuinely fired for this estimate; 'estimate' means it fell back
            to regional averages (missing/invalid key, or NREL's API
            erroring/timing out for this one request) -- shown plainly
            rather than silently, since the production number above is
            meaningfully more accurate in the pvwatts case. */}
        <p className="data-source-note">
          {system.dataSource === 'pvwatts'
            ? '☀️ Production estimate based on real NREL PVWatts solar irradiance data for your exact location.'
            : `Production estimate based on regional solar averages${state ? ` for ${state}` : ''} (exact-location data temporarily unavailable).`}
        </p>
      </div>

      {/* ── 30-YEAR CHART ── */}
      <div className="results-card">
        <div className="card-section-label">30-YEAR SAVINGS PROJECTION</div>
        <p className="chart-note">Assumes 4% annual utility rate increase. Solar payment stays fixed.</p>
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="savingsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={primary} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} label={{ value: 'Years', position: 'insideBottom', offset: -4, fontSize: 11, fill: '#94a3b8' }} />
              <YAxis tickFormatter={v => `$${Math.abs(v) >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`} tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => [fmtDollar(v), 'Cumulative Savings']} labelFormatter={l => `Year ${l}`} contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
              {paybackYear && <ReferenceLine x={paybackYear} stroke="#0f172a" strokeDasharray="4 2" label={{ value: 'Break-even', position: 'top', fontSize: 10, fill: '#0f172a' }} />}
              <Area type="monotone" dataKey="cumulativeSavings" stroke={primary} strokeWidth={2} fill="url(#savingsGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── CTA ── */}
      {!popup && !internalMode && (
        embedded ? (
          <div className="results-card cta-card-embed">
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: 'white', marginBottom: 8 }}>
                {cta.ctaHeadline || 'Ready to Go Solar?'}
              </h3>
              <p style={{ color: '#bfe3cf', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
                {cta.ctaSubtext || 'Our team will design a custom solar system — free, no obligation.'}
              </p>
              {cta.companyName && (
                <p style={{ color: '#a8c3b5', fontSize: 13, marginBottom: 16, fontWeight: 600 }}>{cta.companyName}</p>
              )}
              <a
                href={cta.ctaButtonUrl || (cta.ctaPhone ? `tel:${cta.ctaPhone}` : '#')}
                style={{ display: 'inline-block', padding: '14px 32px', background: 'linear-gradient(135deg, #16a34a, #15803d)', color: 'white', borderRadius: 10, fontWeight: 700, fontSize: 16, textDecoration: 'none', boxShadow: '0 4px 14px rgba(22,163,74,0.4)' }}
              >
                {cta.ctaButtonText || 'Contact Us'}
              </a>
              {cta.ctaPhone && <p style={{ color: '#a8c3b5', fontSize: 14, marginTop: 12 }}>{cta.ctaPhone}</p>}
            </div>
          </div>
        ) : (
          <div className="results-card cta-card-public">
            <PublicEmailCapture results={results} form={form} savings={savings} system={system} />
          </div>
        )
      )}

      {/* ── BOTTOM ACTION BAR ── */}
      {!popup && (
        <div className="bottom-actions">
          <button className="action-btn" onClick={onReset}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 7, flexShrink: 0 }}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
            New Estimate
          </button>
          {!internalMode && (
            <button className="action-btn" onClick={handleShare}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 7, flexShrink: 0 }}><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              Share Results
            </button>
          )}
          <button className="action-btn" onClick={handlePrint}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 7, flexShrink: 0 }}><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Print
          </button>
          {onShareEmail && (
            <button className="action-btn" onClick={() => setShareEmailOpen(v => !v)}>
              <MailIcon size={15} style={{ marginRight: 7, flexShrink: 0 }} />
              Email to Customer
            </button>
          )}
        </div>
      )}

      {onShareEmail && shareEmailOpen && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={shareClientName}
            onChange={e => setShareClientName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submitShareEmail()}
            placeholder="Customer's name"
            style={shareInputStyle}
          />
          <input
            type="email"
            value={shareEmailValue}
            onChange={e => setShareEmailValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submitShareEmail()}
            placeholder="customer@email.com"
            style={{ ...shareInputStyle, minWidth: 220 }}
          />
          <button
            onClick={submitShareEmail}
            disabled={shareEmailStatus === 'sending'}
            className="action-btn"
            style={{ background: primary, color: 'white', borderColor: primary }}
          >
            {shareEmailStatus === 'sending' ? 'Sending…' : shareEmailStatus === 'sent' ? 'Sent ✓' : 'Send'}
          </button>
          {shareEmailStatus === 'error' && (
            <p style={{ width: '100%', textAlign: 'center', color: '#dc2626', fontSize: 12.5, margin: 0 }}>Failed to send. Please try again.</p>
          )}
        </div>
      )}

      <p className="results-disclaimer">
        These estimates are for informational purposes only. Actual prices vary. Always get multiple quotes from licensed, insured professionals.
      </p>
    </div>
  );
}

/* ── Main export ───────────────────────────────────────── */
export default function ResultsScreen({ results, onReset, form, lead, installerConfig, embedded, popup, internalMode, onShareEmail }) {
  const { system, cost, incentives, savings, chart } = results;
  const cta = installerConfig || {};
  const primary = cta.primaryColor || '#1b4d3e';
  const primaryDark = darkenHex(primary);
  const isCash = lead?.paymentMethod === 'cash';

  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!embedded) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [embedded]);

  const handleViewFullReport = () => {
    const fullReportUrl = buildFullReportUrl(results, form, lead, true, installerConfig);
    if (window !== window.parent) {
      window.parent.postMessage({ type: 'MSW_OPEN_REPORT', url: fullReportUrl }, '*');
    } else {
      setShowModal(true);
    }
  };

  /* ── Compact embed view ── (internalMode always gets the full report
     below instead, even when embedded=true for the dashboard tab's compact
     step-form chrome — an installer pricing their own job needs the full
     breakdown, not the tiny public-widget summary.) */
  if (embedded && !internalMode) {
    const hasFinancing = !isCash && savings.monthlyPaymentFinanced > 0;
    const daySavings = hasFinancing ? savings.netMonthlyFinanced : savings.monthly;

    return (
      <>
        <div style={{ padding: '28px 20px 32px', fontFamily: installerConfig?.fontFamily ? `'${installerConfig.fontFamily}', sans-serif` : "'Poppins', sans-serif", maxWidth: 540, margin: '0 auto' }}>

          {/* Mini hero — range estimate, not exact */}
          <div style={{ background: `linear-gradient(135deg, ${primary}, ${primaryDark})`, borderRadius: 16, padding: '20px 20px 18px', marginBottom: 16, textAlign: 'center', color: 'white' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.75, marginBottom: 8 }}>
              Solar Estimate
            </div>
            <div style={{ fontSize: isCash ? 24 : 30, fontWeight: 800, lineHeight: 1.15, marginBottom: 6 }}>
              {isCash
                ? <>{fmtDollar(incentives.netCostLow)} – {fmtDollar(incentives.netCostHigh)} installed cost</>
                : <>Save <span style={{ color: '#86efac' }}>{fmtDollar(daySavings)}/mo</span> from day one</>
              }
            </div>
            <div style={{ fontSize: 12, opacity: 0.8, lineHeight: 1.5 }}>
              {isCash
                ? `${fmtDollar(savings.annual)}/yr savings · paid off in ${savings.paybackYears} yrs`
                : `installed cost ${fmtDollar(incentives.netCostLow)}–${fmtDollar(incentives.netCostHigh)} · ${savings.paybackYears} yr payback`
              }
            </div>
          </div>

          {/* Key metrics — 2×2 grid, monthly savings replaces net cost */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 14 }}>
            {[
              { label: 'Monthly Savings', value: fmtDollar(savings.monthly) + '/mo' },
              { label: 'Annual Savings',  value: fmtDollar(savings.annual) },
              { label: '30-Year Savings', value: fmtDollar(savings.thirtyYear), highlight: true },
              { label: 'Payback Period',  value: savings.paybackYears ? `${savings.paybackYears} yrs` : 'N/A' },
            ].map(({ label, value, highlight }) => (
              <div key={label} style={{ background: highlight ? primary : '#f8fafc', border: `1px solid ${highlight ? primary : '#e2e8f0'}`, borderRadius: 10, padding: '11px 10px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: highlight ? 'rgba(255,255,255,0.75)' : '#64748b', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: highlight ? 'white' : '#0f172a' }}>{value}</div>
              </div>
            ))}
          </div>

          {/* System pill + installation range */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#475569', lineHeight: 1.7 }}>
            <div><strong style={{ color: '#0f172a' }}>{system.sizeKw} kW system</strong> · {system.panelCount} panels · {system.offsetPercent}% energy offset</div>
            <div style={{ color: '#64748b' }}>Installation: <span style={{ color: primary, fontWeight: 600 }}>{fmtDollar(cost.low)} – {fmtDollar(cost.high)}</span></div>
            {/* Same dataSource transparency as the full report (ReportContent
                below), kept to one compact line for the embedded widget. */}
            <div style={{ color: '#94a3b8', fontSize: 10.5, marginTop: 2 }}>
              {system.dataSource === 'pvwatts' ? '☀️ Based on real NREL data for this location' : 'Based on regional solar averages'}
            </div>
          </div>

          {/* Installer CTA */}
          <div style={{ background: primaryDark, borderRadius: 12, padding: '14px 16px', marginBottom: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'white', marginBottom: 2 }}>
                  {cta.ctaHeadline || 'Ready to Go Solar?'}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>
                  {cta.companyName || cta.ctaSubtext || 'Free, no obligation quote'}
                </div>
              </div>
              <a
                href={cta.ctaButtonUrl || (cta.ctaPhone ? `tel:${cta.ctaPhone}` : '#')}
                style={{ display: 'inline-block', padding: '9px 18px', background: primary, color: 'white', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none', flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {cta.ctaButtonText || 'Contact Us'}
              </a>
            </div>
            {cta.ctaPhone && (
              <div style={{ fontSize: 11, color: '#475569', marginTop: 8, textAlign: 'center' }}>{cta.ctaPhone}</div>
            )}
          </div>

          {/* Full report + recalc */}
          <div style={{ textAlign: 'center' }}>
            <button onClick={handleViewFullReport} style={{ fontSize: 15, color: primary, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              Open Full Solar Report
            </button>
            <span style={{ color: '#cbd5e1', margin: '0 8px' }}>·</span>
            <button onClick={onReset} style={{ fontSize: 13, color: '#64748b', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              Recalculate
            </button>
          </div>
        </div>

        {showModal && (
          <FullReportModal results={results} form={form} lead={lead} installerConfig={installerConfig} onClose={() => setShowModal(false)} onReset={() => { setShowModal(false); onReset(); }} />
        )}
      </>
    );
  }

  /* ── Full page view ── (also used for internalMode, since it needs the
     full breakdown rather than the compact widget above; internalMode
     drops the full-bleed page chrome since it's rendered inside a
     dashboard card, not a standalone page) */
  return (
    <section className="results-section" style={internalMode ? { minHeight: 'auto', background: 'transparent', padding: '24px 20px 32px' } : undefined}>
      <ReportContent
        results={results}
        form={form}
        lead={lead}
        installerConfig={installerConfig}
        onReset={onReset}
        embedded={embedded}
        popup={popup}
        internalMode={internalMode}
        onShareEmail={onShareEmail}
      />

      {showModal && (
        <FullReportModal results={results} form={form} lead={lead} installerConfig={installerConfig} onClose={() => setShowModal(false)} onReset={() => { setShowModal(false); onReset(); }} />
      )}
    </section>
  );
}

/* ── Full Report Modal ─────────────────────────────────── */
function FullReportModal({ results, form, lead, installerConfig, onClose, onReset }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '16px 0 40px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#f1f5f9', width: '100%', maxWidth: 720, borderRadius: 16, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', position: 'relative' }}>
        {/* Sticky header */}
        <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'white', borderBottom: '1px solid #e2e8f0', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Full Solar Report</span>
          <button onClick={onClose} style={{ fontSize: 13, color: '#64748b', fontWeight: 600, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', padding: '6px 14px', fontFamily: 'inherit' }}>
            ✕ Close
          </button>
        </div>

        <section className="results-section">
          <ReportContent
            results={results}
            form={form}
            lead={lead}
            installerConfig={installerConfig}
            onReset={onReset}
            embedded={false}
            popup={false}
          />
        </section>
      </div>
    </div>
  );
}

/* ── Public email capture ──────────────────────────────── */
function PublicEmailCapture({ results, form, savings, system }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) { setError('Please enter a valid email address.'); return; }
    setError('');
    setLoading(true);
    try {
      await supabase.from('leads').insert({
        installer_id: null,
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: null,
        timeline: null,
        payment_method: null,
        monthly_bill: parseFloat(form.monthlyBill),
        state: form.state || null,
        zip: form.zip || null,
        system_size_kw: system?.sizeKw || null,
        annual_savings: savings?.annual || null,
        total_cost: results?.cost?.total || null,
      });
    } catch (_) {
      // fire-and-forget
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="lead-success">
        <div style={{ marginBottom: 12 }}><CheckCircleIcon size={40} color="#16a34a" /></div>
        <h3>Report sent!</h3>
        <p>Check your inbox for your personalized solar savings summary.</p>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', padding: '4px 0 8px' }}>
      <div style={{ marginBottom: 10 }}><MailIcon size={26} color="white" /></div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: 'white', marginBottom: 6 }}>Send report to your email</h3>
      <p style={{ color: '#bfe3cf', fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
        Get your personalized solar savings summary delivered to your inbox.
      </p>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 9, maxWidth: 340, margin: '0 auto' }}>
        <input type="text" placeholder="Full Name" value={name} onChange={e => { setName(e.target.value); setError(''); }} style={inputStyle} />
        <input type="email" placeholder="Email address" value={email} onChange={e => { setEmail(e.target.value); setError(''); }} style={inputStyle} />
        {error && <p style={{ color: '#fca5a5', fontSize: 13, margin: 0 }}>{error}</p>}
        <button
          type="submit"
          disabled={loading}
          style={{ padding: '12px 24px', background: loading ? '#15803d' : '#16a34a', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.85 : 1, boxShadow: '0 3px 10px rgba(22,163,74,0.4)' }}
        >
          {loading ? 'Sending...' : 'Send My Report →'}
        </button>
      </form>
    </div>
  );
}

const shareInputStyle = {
  padding: '9px 13px',
  borderRadius: 10,
  border: '1.5px solid #e2e8f0',
  background: 'white',
  color: '#0f172a',
  fontSize: 13.5,
  outline: 'none',
  minWidth: 160,
};

const inputStyle = {
  padding: '11px 13px',
  borderRadius: 10,
  border: '1.5px solid rgba(255,255,255,0.25)',
  background: 'rgba(255,255,255,0.15)',
  color: 'white',
  fontSize: 14,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  caretColor: 'white',
};
